/**
 * Random sources. Production code uses the Web Crypto API (`globalThis.crypto.getRandomValues`, available in
 * browsers and Node >= 19); tests may inject a seeded source for reproducibility.
 */
export interface RandomSource {
  /** `n` random bytes. */
  bytes(n: number): Uint8Array;
}

export function cryptoRandomSource(): RandomSource {
  const c = (globalThis as { crypto?: { getRandomValues?<T extends ArrayBufferView>(a: T): T } }).crypto;
  if (!c || typeof c.getRandomValues !== 'function') {
    throw new Error('Web Crypto getRandomValues is not available; inject a RandomSource');
  }
  return {
    bytes(n: number): Uint8Array {
      const out = new Uint8Array(n);
      for (let off = 0; off < n; off += 65536) c.getRandomValues!(out.subarray(off, Math.min(n, off + 65536)));
      return out;
    },
  };
}

/** Deterministic PRNG (mulberry32) — tests only, never for secrets. */
export function seededRandomSource(seed: number): RandomSource {
  let a = seed >>> 0;
  const next = (): number => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return (t ^ (t >>> 14)) >>> 0;
  };
  return {
    bytes(n: number): Uint8Array {
      const out = new Uint8Array(n);
      for (let i = 0; i < n; i++) out[i] = next() & 0xff;
      return out;
    },
  };
}

/** Uniform integer in [0, maxExclusive) without modulo bias. */
export function randomInt(rng: RandomSource, maxExclusive: number): number {
  if (!Number.isInteger(maxExclusive) || maxExclusive <= 0 || maxExclusive > 0x100000000) throw new RangeError('randomInt: bad range');
  const limit = Math.floor(0x100000000 / maxExclusive) * maxExclusive;
  for (;;) {
    const b = rng.bytes(4);
    const v = ((b[0] << 24) | (b[1] << 16) | (b[2] << 8) | b[3]) >>> 0;
    if (v < limit) return v % maxExclusive;
  }
}

const B32 = '0123456789abcdefghjkmnpqrstvwxyz';

/** Random identifier: optional prefix + Crockford base32 (lowercase), `bytes` * 8 bits of entropy. */
export function randomId(rng: RandomSource, prefix = '', bytes = 10): string {
  const b = rng.bytes(bytes);
  let bits = 0;
  let acc = 0;
  let out = '';
  for (const x of b) {
    acc = (acc << 8) | x;
    bits += 8;
    while (bits >= 5) {
      out += B32[(acc >>> (bits - 5)) & 31];
      bits -= 5;
    }
    acc &= (1 << bits) - 1;
  }
  if (bits > 0) out += B32[(acc << (5 - bits)) & 31];
  return prefix + out;
}

/** Random bearer secret, base64url without padding. */
export function randomToken(rng: RandomSource, bytes = 32): string {
  const b = rng.bytes(bytes);
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';
  let out = '';
  let i = 0;
  for (; i + 2 < b.length; i += 3) {
    const n = (b[i] << 16) | (b[i + 1] << 8) | b[i + 2];
    out += alphabet[(n >> 18) & 63] + alphabet[(n >> 12) & 63] + alphabet[(n >> 6) & 63] + alphabet[n & 63];
  }
  if (i < b.length) {
    const n = (b[i] << 16) | ((i + 1 < b.length ? b[i + 1] : 0) << 8);
    out += alphabet[(n >> 18) & 63] + alphabet[(n >> 12) & 63];
    if (i + 1 < b.length) out += alphabet[(n >> 6) & 63];
  }
  return out;
}
